import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import * as c from "../../../../constants";
import {
  Box,
  IconButton,
  Typography,
  Stack,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { ClipboardList } from "lucide-react";
import { useTranslation } from "../../../../language";
import ButtonsMain from "../../../../components/Button/ButtonMain";
import NewQuestionnaireDrawer from "./NewQuestionnaireDrawer";

const CAROUSEL_VISIBLE = 2;

function parseDateParts(isoString, locale) {
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return { month: "—", day: "—", year: "—" };
  return {
    month: d.toLocaleString(locale, { month: "short" }).toUpperCase(),
    day: String(d.getDate()).padStart(2, "0"),
    year: String(d.getFullYear()),
  };
}

function DateCarousel({ dates, selectedDate, onSelect }) {
  const theme = useTheme();
  const { language } = useTranslation();
  const reversed = [...dates].reverse();
  const [startIndex, setStartIndex] = useState(Math.max(0, reversed.length - CAROUSEL_VISIBLE));

  useEffect(() => {
    setStartIndex(Math.max(0, reversed.length - CAROUSEL_VISIBLE));
  }, [reversed.length]);

  const canPrev = startIndex > 0;
  const canNext = startIndex + CAROUSEL_VISIBLE < reversed.length;
  const visible = reversed.slice(startIndex, startIndex + CAROUSEL_VISIBLE);

  return (
    <Box sx={{
      display: "flex", alignItems: "center",
      borderRadius: 4, px: 1, py: 1.5,
      bgcolor: theme.palette.mode === "dark" ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
      backdropFilter: "blur(28px) saturate(180%)",
      WebkitBackdropFilter: "blur(28px) saturate(180%)",
      border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.65)"}`,
      boxShadow: theme.palette.mode === "dark"
        ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
        : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
    }}>
      <IconButton size="small" onClick={() => setStartIndex(i => i - 1)} disabled={!canPrev}
        sx={{ color: theme.palette.mode === "light" ? "greydark.C900" : "grey.C50" }}>
        <ChevronLeftIcon />
      </IconButton>
      <Box sx={{ display: "flex", gap: 1.5, mx: 0.5 }}>
        {visible.map((date) => {
          const { month, day, year } = parseDateParts(date, language);
          const selected = date === selectedDate;
          return (
            <Box
              key={date}
              onClick={() => onSelect(date)}
              sx={{
                cursor: "pointer",
                width: 82, py: selected ? 1.75 : 1.25, px: 0.5, mx: selected ? 1.5 : 0,
                borderRadius: 3,
                border: "2px solid",
                borderColor: selected ? "primary.main" : (theme.palette.mode === "light" ? "grey.C900" : "greydark.C400"),
                display: "flex", flexDirection: "column", alignItems: "center",
                bgcolor: selected ? `${theme.palette.primary.main}18` : "transparent",
                transform: selected ? "scale(1.1)" : "scale(1)",
                boxShadow: selected ? `0 4px 18px ${theme.palette.primary.main}40` : "none",
                zIndex: selected ? 1 : 0,
                transition: "all 0.2s ease", userSelect: "none",
              }}
            >
              <Typography variant="caption" fontWeight={700} sx={{
                color: selected ? "primary.main" : "text.secondary",
                letterSpacing: 0.5, fontSize: selected ? "0.8rem" : "0.72rem",
              }}>
                {month}
              </Typography>
              <Typography variant="h5" fontWeight={800} sx={{
                lineHeight: 1.15, my: 0.25,
                color: selected ? "primary.main" : "text.primary",
                fontSize: selected ? "1.75rem" : "1.35rem",
              }}>
                {day}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: selected ? "0.8rem" : "0.72rem" }}>
                {year}
              </Typography>
            </Box>
          );
        })}
      </Box>
      <IconButton size="small" onClick={() => setStartIndex(i => i + 1)} disabled={!canNext}
        sx={{ color: theme.palette.mode === "light" ? "greydark.C900" : "grey.C50" }}>
        <ChevronRightIcon />
      </IconButton>
    </Box>
  );
}

export default function Responses() {
  const { t } = useTranslation();
  const theme = useTheme();

  const [questionnaire, setQuestionnaire] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCreatedAt, setSelectedCreatedAt] = useState("");
  const [error, setError] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let isMounted = true;

    axios
      .get(`${c.API_URL}/api/responses/`)
      .then((res) => {
        if (!isMounted) return;
        const data = res.data || [];
        setQuestionnaire(data);
        if (data.length > 0) {
          const newest = [...data].sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          )[0];
          setSelectedCreatedAt(newest.created_at);
        }
      })
      .catch(() => {
        if (isMounted) setError(true);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [refreshKey]);

  const sortedQuestionnaires = useMemo(
    () => [...questionnaire].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [questionnaire]
  );

  const selectedQuestionnaire = useMemo(
    () => questionnaire.find((item) => item.created_at === selectedCreatedAt),
    [selectedCreatedAt, questionnaire]
  );

  const isDark = theme.palette.mode === "dark";
  const cardStyle = {
    borderRadius: 4,
    overflow: "hidden",
    bgcolor: isDark ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
    backdropFilter: "blur(28px) saturate(180%)",
    WebkitBackdropFilter: "blur(28px) saturate(180%)",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.65)"}`,
    boxShadow: isDark
      ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
      : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
  };

  const questionBg = isDark ? alpha("#fff", 0.03) : alpha("#fff", 0.35);
  const answerBg   = isDark ? alpha("#fff", 0.06) : alpha("#fff", 0.6);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography color="error">{t("bt_results_error")}</Typography>
      </Box>
    );
  }

  if (!questionnaire.length) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography>{t("bt_questionnaire_empty")}</Typography>
      </Box>
    );
  }

  return (
    <>
    <NewQuestionnaireDrawer
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      onSuccess={() => setRefreshKey((k) => k + 1)}
    />
    <Box sx={{ width: "100%", maxWidth: { md: 800 }, mx: "auto", px: { xs: 2, sm: 4 }, pt: { xs: 1, sm: 2 }, pb: 6 }}>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
        <ButtonsMain
          action={() => setDrawerOpen(true)}
          name={t("quest_new_btn")}
          startIcon={<ClipboardList size={16} />}
          sx={{ px: "24px" }}
        />
      </Box>
      <Typography variant="subtitle1" textAlign="center" sx={{ fontWeight: 400, mb: 3 }}>
        {t("account_quest1")}
      </Typography>

      <Box sx={{ mb: 4, display: "flex", justifyContent: "center" }}>
        <DateCarousel
          dates={sortedQuestionnaires.map((item) => item.created_at)}
          selectedDate={selectedCreatedAt}
          onSelect={setSelectedCreatedAt}
        />
      </Box>

      {selectedQuestionnaire && (
        <Stack spacing={2}>
          {selectedQuestionnaire.answers.map((answer, idx) => {
            const accentColor = idx % 2 === 0 ? theme.palette.tertiary.C300 : theme.palette.quartiary.C300;
            return (
              <Box key={answer.question_key} sx={{ ...cardStyle, borderLeft: `10px solid ${accentColor}` }}>
                <Box sx={{ px: 2, py: 1, bgcolor: questionBg }}>
                  <Typography variant="subtitle1" fontWeight={400}>
                    {answer.question_text}
                  </Typography>
                </Box>
                <Box sx={{ px: 2, py: 1, bgcolor: answerBg }}>
                  <Typography variant="subtitle1" color="primary.dark">
                    {Array.isArray(answer.value_label)
                      ? answer.value_label.join(", ") || "-"
                      : answer.value_label || "-"}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
    </>
  );
}
